import { useMemo } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import type { Stripe, StripeElementsOptionsClientSecret } from '@stripe/stripe-js';
import { CreditCard, Landmark, type LucideIcon } from 'lucide-react';
import type { PaymentTokenStripeData } from '../api/paymentApi';
import {
  PAYMENT_CARD_CLASS,
  getPaymentMethodKind,
  type PaymentAmounts,
  type PaymentMethodKind,
} from '../utils/paymentDisplay';
import { CardPaymentForm } from './CardPaymentForm';
import { SepaPaymentForm } from './SepaPaymentForm';
import { SepaSetupForm } from './SepaSetupForm';
import { AmountHighlight } from './PaymentAmountSummary';
import { IconBadge } from './PaymentUi';

const METHOD_COPY: Record<PaymentMethodKind, { icon: LucideIcon; title: string; hint: string }> = {
  sepa_mandate: {
    icon: Landmark,
    title: 'Prélèvement SEPA mensuel',
    hint: 'Renseignez votre IBAN pour mettre en place le mandat de prélèvement.',
  },
  sepa_single: {
    icon: Landmark,
    title: 'Paiement par prélèvement SEPA',
    hint: 'Renseignez votre IBAN pour régler votre contrat.',
  },
  card: {
    icon: CreditCard,
    title: 'Paiement par carte bancaire',
    hint: 'Renseignez les informations de votre carte.',
  },
};

interface PaymentMethodCardProps {
  data: PaymentTokenStripeData;
  stripePromise: Promise<Stripe | null>;
  amounts: PaymentAmounts | null;
  onSuccess: () => void;
  className?: string;
}

/**
 * Carte de paiement : formulaire Stripe choisi selon l'intent (inchangé),
 * thème Stripe et bouton aux couleurs du garant quand elles sont connues.
 */
export function PaymentMethodCard({ data, stripePromise, amounts, onSuccess, className = '' }: PaymentMethodCardProps) {
  const methodKind = getPaymentMethodKind(data);
  const copy = METHOD_COPY[methodKind];
  const branding = data.upsell?.branding ?? null;
  const accentColor = branding?.accentColor || undefined;
  const accentTextColor = branding?.accentTextColor || undefined;
  const companyName = data.metadata?.companyName;

  // Options stables : Elements n'accepte pas un changement de clientSecret après montage.
  const elementsOptions = useMemo(() => {
    const options: StripeElementsOptionsClientSecret = { clientSecret: data.clientSecret };
    if (accentColor) {
      options.appearance = { theme: 'stripe', variables: { colorPrimary: accentColor, borderRadius: '8px' } };
    }
    return options;
  }, [data.clientSecret, accentColor]);

  return (
    <section aria-labelledby="payment-method-title" className={`${PAYMENT_CARD_CLASS} tw-overflow-hidden ${className}`}>
      <div className="tw-border-b tw-border-slate-100 tw-px-5 tw-py-5 sm:tw-px-6">
        <div className="tw-flex tw-items-center tw-gap-3">
          <IconBadge icon={copy.icon} />
          <div className="tw-min-w-0">
            <h2 id="payment-method-title" className="tw-mb-0 tw-text-lg tw-font-semibold tw-text-gray-900">
              {copy.title}
            </h2>
            <p className="tw-mb-0 tw-text-sm tw-text-gray-600">{copy.hint}</p>
          </div>
        </div>
        {amounts && (
          <div className="tw-mt-4 tw-hidden lg:tw-block">
            <AmountHighlight amounts={amounts} />
          </div>
        )}
      </div>

      <div className="tw-px-5 tw-py-5 sm:tw-px-6 sm:tw-py-6">
        <Elements stripe={stripePromise} options={elementsOptions}>
          {methodKind === 'sepa_mandate' ? (
            <SepaSetupForm
              clientSecret={data.clientSecret}
              onSuccess={onSuccess}
              companyName={companyName}
              monthlyAmount={data.metadata?.monthlyAmount}
              duration={data.metadata?.duration}
              accentColor={accentColor}
              accentTextColor={accentTextColor}
            />
          ) : methodKind === 'sepa_single' ? (
            <SepaPaymentForm
              clientSecret={data.clientSecret}
              onSuccess={onSuccess}
              sellingPrice={data.metadata?.sellingPrice}
              companyName={companyName}
              accentColor={accentColor}
              accentTextColor={accentTextColor}
            />
          ) : (
            <CardPaymentForm
              clientSecret={data.clientSecret}
              onSuccess={onSuccess}
              sellingPrice={data.metadata?.sellingPrice}
              companyName={companyName}
              accentColor={accentColor}
              accentTextColor={accentTextColor}
            />
          )}
        </Elements>
      </div>
    </section>
  );
}
